import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button } from "@mui/material";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import {
  MyTimesheetMyRequestsDashboardSummary,
  MyTimesheetMyRequestsDashboardTable,
} from "my-timesheet/components";
import { useMyTimesheetTimesheetList } from "my-timesheet/hooks";
import { OcCard, OcChart, OcChartData, OcIconForState } from "oc/components";
import { useOcCurrentUserState } from "oc/hooks";
import useOcButtonGroup from "oc/hooks/use-button-group";

ChartJS.register(ArcElement, Tooltip, Legend);

interface MyTimesheetMyRequestsDashboardProps {
  periodStart: string; // Adjust the type as per your actual data (e.g., Date, string, etc.)
  periodEnd: string; // Adjust the type as per your actual data
  isMobile: boolean;
  onPrint?: () => void;
}

export const MyTimesheetMyRequestsDashboard: React.FC<
  MyTimesheetMyRequestsDashboardProps
> = ({ periodStart, periodEnd, isMobile, onPrint }) => {
  const { t } = useTranslation();

  const buttonsDef = ["MY_TIMESHEET_PROJECT", "MY_TIMESHEET_CLIENT", "OC_COST"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: "MySheetMyRequestsDashboard",
    buttonsDef,
  });

  let { contractCode } = useOcCurrentUserState();
  const {
    summaryByType,
    hoursSummary,
    hoursSummaryBillable,
    costsSummary,
    loading,
    error,
  } = useMyTimesheetTimesheetList({
    contractId: contractCode,
    periodStart: new Date(periodStart),
    periodEnd: new Date(periodEnd),
    dashboard: true,
    role: "user",
  });

  let data: { name: string; summaryTime: string; color: string }[] = [];

  const projectData = summaryByType("projectName");
  const clientData = summaryByType("clientName");
  const costData = summaryByType("costName");

  const HUF = new Intl.NumberFormat("hu-HU", {
    style: "currency",
    currency: "HUF",
    maximumSignificantDigits: 3,
  });

  const getTimeSummary = (value: string) => {
    const [hours, minutes] = value.split(":");
    return minutes === "00"
      ? `${hours} ${t("OC_HOUR").toLowerCase()}`
      : `${hours} ${t("OC_HOUR").toLowerCase()} ${minutes} ${t("MY_TIMESHEET_MINUTE").toLowerCase()}`;
  };

  let chartSummary = "";
  switch (selectedButton) {
    case "MY_TIMESHEET_PROJECT":
      chartSummary = getTimeSummary(hoursSummary);
      data = projectData;
      break;
    case "MY_TIMESHEET_CLIENT":
      chartSummary = getTimeSummary(hoursSummary);
      data = clientData;
      break;
    case "OC_COST":
      chartSummary = HUF.format(costsSummary);
      data = costData;
      break;
    default:
      break;
  }

  const newChartData: OcChartData[] = data.map((item) => {
    const [hours, minutes] = (item?.summaryTime ?? "0:00").split(":");
    const value = Number.parseInt(hours) * 60 + Number.parseInt(minutes);

    return {
      color: item.color,
      label: item.name,
      value: value,
    };
  });

  return (
    <Box>
      {data?.length === 0 && !loading ? (
        <>
          <OcIconForState
            data={data}
            loading={loading}
            isMobile={isMobile}
            error={!!error}
            module="MyTimesheet"
          />
        </>
      ) : (
        <>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              style={{
                width: isMobile ? "100%" : "calc(33% - 8px)",
              }}
            >
              <MyTimesheetMyRequestsDashboardSummary
                label={t("MY_TIMESHEET_TOTAL_TIME")}
                value={getTimeSummary(hoursSummary)}
              />
            </Box>
            {isMobile && <Box height="16px" />}
            <Box
              style={{
                width: isMobile ? "100%" : "calc(33% - 8px)",
              }}
            >
              <MyTimesheetMyRequestsDashboardSummary
                label={t("MY_TIMESHEET_TOTAL_TIME_BILLABLE")}
                value={getTimeSummary(hoursSummaryBillable)}
              />
            </Box>
            {isMobile && <Box height="16px" />}
            <Box
              style={{
                width: isMobile ? "100%" : "calc(33% - 8px)",
              }}
            >
              <MyTimesheetMyRequestsDashboardSummary
                label={t("MY_TIMESHEET_TOTAL_COST")}
                value={HUF.format(costsSummary)}
              />
            </Box>
            {isMobile && <Box height="16px" />}
          </Box>
          <Box height="16px" />
          <OcCard
            label={t("MY_TIMESHEET_SUMMARY_STATEMENT")}
            loading={loading}
            error={!!error}
          >
            <Box height="22px" />
            <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" position="relative">
              <OcButtonGroup />
              {onPrint && (
                <Box position="absolute" right="0">
                  <Button
                    id="print-button"
                    variant="outlined"
                    color="secondary"
                    disableElevation
                    onClick={onPrint}
                  >
                    {t("MY_TIMESHEET_TIMESHEET_REPORT_PRINT")}
                  </Button>
                </Box>
              )}
            </Box>
            <Box height="38px" />
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              alignItems={isMobile ? "center" : "flex-start"}
            >
              <Box flexGrow={1} paddingRight={isMobile ? "0px" : "32px"}>
                <MyTimesheetMyRequestsDashboardTable
                  data={data}
                  type={selectedButton}
                  loading={loading}
                  isMobile={isMobile}
                />
              </Box>
              <Box>
                <OcChart
                  data={newChartData}
                  summaryTitle={chartSummary}
                  summaryText={t("OC_SUMMARY")}
                  variant="MyTimesheet"
                />
              </Box>
            </Box>
          </OcCard>
        </>
      )}
    </Box>
  );
};

export default MyTimesheetMyRequestsDashboard;
